/**
 * Top-level provider for the shared Sources UI.
 *
 * Wraps:
 *   1. The host adapter context (so lib internals can call `useSourcesHost()`).
 *   2. An internal `@tanstack/react-query` v5 QueryClientProvider so source
 *      queries get a client even when the host's surrounding tree doesn't
 *      provide one.
 *
 * Hosts may pass an existing v5 `QueryClient` via the `queryClient` prop to
 * share the cache with other host queries (AIM does this). When omitted,
 * the lib creates its own client with sensible defaults.
 *
 * This lib was migrated v3 → v5 alongside the host apps. Before
 * that migration AIM was on v3 and SMS on v5; the lib stayed on v3 with
 * its own internal client. Post-migration both hosts are v5 and the lib
 * matches, which is what `client.defaultQueryObserverOptions` would have
 * told us if we'd routed through it sooner.
 */
import { useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { SourcesHost } from '../types/host';
import { SourcesHostProvider } from './SourcesHostContext';

const defaultClientFactory = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  });

export interface SourcesProviderProps {
  host: SourcesHost;
  /** Optional existing `@tanstack/react-query` v5 QueryClient to share cache with the host. */
  queryClient?: QueryClient;
  children: React.ReactNode;
}

export function SourcesProvider({
  host,
  queryClient,
  children,
}: SourcesProviderProps) {
  const client = useMemo(
    () => queryClient ?? defaultClientFactory(),
    [queryClient],
  );
  return (
    <QueryClientProvider client={client}>
      <SourcesHostProvider host={host}>{children}</SourcesHostProvider>
    </QueryClientProvider>
  );
}
